import React from "react"
import { wrapPage } from "../../aggregators/page"
import { RootData } from "../../aggregators/rootData/context"
import { fetchSerpContextData } from "../../aggregators/serp/fetch"
import { SerpContextData } from "../../aggregators/serp/types"
import BrandEditorialContainer from "../../containers/brand"
import BrandPageHead from "../../containers/common/BrandPageHead"
import BrandPageIntro from "../../containers/common/BrandPageIntro"
import PageNotFoundContainer from "../../containers/errors/PageNotFoundContainer"
import AlgoliaSerpContainerWrapper from "../../containers/search"
import { fetchSsrSearchData } from "../../containers/search/components/controller/state/ssr"
import { AlgoliaStaticSearchResult } from "../../containers/search/components/controller/state/types"
import AlgoliaStaticSearch from "../../containers/search/components/staticSearch"
import SerpContainerWrapper from "../../containers/serp"
import { fetchBrandPageData } from "../../contents/brand/fetch"
import { mergeBrandCodes, toCustomCategoriesDict } from "../../converters/serp"
import { useProductSearchType } from "../../features/search/hooks/searchType"
import GenericSEO from "../../features/seo/components/GenericSEO"
import { SeoContents } from "../../features/seo/components/PageSEO"
import { DefaultLayout } from "../../layouts/Default/DefaultLayout"
import { WrapPage } from "../../root"
import { sanitizeNextObject } from "../../utils/serialization"

interface Props {
  slug: string
  path: string
  contents: any
  languageId: string
  rootData: RootData
  serpData: SerpContextData
  staticSearch?: AlgoliaStaticSearchResult
}

const getParams = (data: any, languageId: string) => ({
  title: data.data.info.title,
  breadcrumb: [],
  languageId,
  contents: data,
  brandCodes: mergeBrandCodes(data.ref.brand, data.ref.secondaryBrands),
  customCategories: toCustomCategoriesDict(data.ref.customCategories),
  heroClassName: data.ref.brand?.data?.logo?.asset ? "mb-4" : undefined,
  heroLogo: <BrandPageHead contents={data} />,
  hideHero: !!data.ref?.brand?.data?.header?.logo?.asset,
  intro: data.ref?.brand?.data?.header?.logo?.asset ? (
    <></>
  ) : (
    <BrandPageIntro contents={data} />
  ),
})

export default function BrandPage({
  path,
  languageId,
  contents: data,
  rootData,
  serpData,
  staticSearch,
}: Props) {
  // const { data } = usePreviewSubscription(brandPageDataQuery, {
  //   params: {
  //     locale: languageId,
  //     slug,
  //   },
  //   initialData: contents,
  //   enabled: isPreviewSession(),
  // })

  const searchType = useProductSearchType(rootData)
  return (
    <WrapPage rootData={rootData}>
      <DefaultLayout>
        {data.data ? (
          <>
            <BrandEditorialContainer
              brandItem={data.ref.brand}
              menuItems={data.data?.navigationMenu?.items}
              contents={data.data?.navigationMenu?.contents}
            />

            <>
              {searchType === "algolia" ? (
                <AlgoliaSerpContainerWrapper
                  {...getParams(data, languageId)}
                  path={path}
                  serpData={serpData}
                />
              ) : (
                <SerpContainerWrapper {...getParams(data, languageId)} />
              )}
              {staticSearch && (
                <noscript>
                  <AlgoliaStaticSearch staticSearch={staticSearch} />
                </noscript>
              )}
            </>

            <SeoContents page={data} />
          </>
        ) : (
          <>
            <PageNotFoundContainer />
            <GenericSEO
              title="Pagina non trovata"
              description="Pagina non trovata"
              noIndex
            />
          </>
        )}
      </DefaultLayout>
    </WrapPage>
  )
}

interface GetStaticSearchInput {
  contents: any
  rootData: RootData
  serpContextData: SerpContextData
  query: any
}

const getStaticSearch = async (input: GetStaticSearchInput) => {
  const { contents, query, rootData, serpContextData } = input
  const brandCodes = contents.ref.brand
    ? [contents.ref.brand.data.code as string]
    : undefined
  return brandCodes
    ? await fetchSsrSearchData({
        params: {
          brandCodes,
          breadcrumb: [],
          title: contents?.data.info.title,
        },
        rootData,
        serpContextData,
        query,
      })
    : undefined
}

export async function getServerSideProps({ params, locale, query }: any) {
  return await wrapPage({
    locale,
    fetchProps: async (languageId: string, rootData: RootData) => {
      const contents = await fetchBrandPageData(languageId, params.slug)
      const serpContextData = await fetchSerpContextData(languageId)
      const staticSearch = await getStaticSearch({
        contents,
        query,
        rootData,
        serpContextData,
      })
      return {
        contents: sanitizeNextObject(contents),
        path: sanitizeNextObject(params.path),
        slug: sanitizeNextObject(params.slug),
        serpData: sanitizeNextObject(serpContextData),
        staticSearch: sanitizeNextObject(staticSearch),
      }
    },
  })
}
